import './MembershipsOverview.css';

import ModuleNotEnabledState from '../../../shared/components/ModuleNotEnabledState';
import { useState } from 'react';
import MembershipActionsTab from '../components/MembershipActionsTab';
import MembershipAnalyticsStrip from '../components/MembershipAnalyticsStrip';
import MembershipBenefitsTable from '../components/MembershipBenefitsTable';
import MembershipTypeMembersTab from '../components/MembershipTypeMembersTab';
import MembershipTypeHeader from '../components/MembershipTypeHeader';
import MembershipTypeListTable from '../components/MembershipTypeListTable';
import {
  useActivateMembershipType,
  useDeactivateMembershipType,
  useMembershipAnalytics,
  useMembershipTypes
} from '../queries';

type MembershipsOverviewPageProps = {
  membershipEnabled: boolean;
  onNavigate?: (path: string) => void;
};

const MembershipsOverviewPage = ({ membershipEnabled, onNavigate }: MembershipsOverviewPageProps) => {
  const [activeTab, setActiveTab] = useState<'tiers' | 'members' | 'actions'>('tiers');
  const analyticsQuery = useMembershipAnalytics(membershipEnabled);
  const typesQuery = useMembershipTypes(membershipEnabled);
  const activateType = useActivateMembershipType();
  const deactivateType = useDeactivateMembershipType();
  const memberTypeOptions = (typesQuery.data?.items || []).map((row) => ({
    value: row.membershipTypeUuid,
    label: row.name,
  }));
  const memberCountByType = new Map((analyticsQuery.data?.byTier || []).map((row) => [row.membershipTypeUuid, row.activeCount]));
  const totalTierCount = typesQuery.data?.totalElements ?? typesQuery.data?.items?.length;

  if (!membershipEnabled) {
    return (
      <ModuleNotEnabledState
        title="Memberships are not enabled"
        description="Upgrade your plan to launch membership tiers, points, and customer loyalty journeys."
      />
    );
  }

  return (
    <section className="appointments-v2-page" aria-live="polite">
      <MembershipTypeHeader
        variant="overview"
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onPrimaryAction={() => onNavigate?.('/store/memberships/new')}
        tabCounts={{
          tiers: totalTierCount,
          members: analyticsQuery.data?.activeCount,
        }}
      />

      {activeTab === 'tiers' ? (
        <div className="membership-tab-content" key="tiers">
          <MembershipAnalyticsStrip
            analytics={analyticsQuery.data}
            loading={analyticsQuery.isLoading}
            error={analyticsQuery.isError ? 'Unable to load membership analytics right now.' : ''}
          />
          <MembershipTypeListTable
            rows={typesQuery.data?.items || []}
            analytics={analyticsQuery.data}
            loading={typesQuery.isLoading}
            onView={(row) => onNavigate?.(`/store/memberships/${row.membershipTypeUuid}`)}
            onEdit={(row) => onNavigate?.(`/store/memberships/${row.membershipTypeUuid}/edit`)}
            onToggleActive={(row) => {
              if (row.isActive) {
                deactivateType.mutate(row.membershipTypeUuid);
                return;
              }
              activateType.mutate(row.membershipTypeUuid);
            }}
            onCreateNew={() => onNavigate?.('/store/memberships/new')}
          />
          <MembershipBenefitsTable tiers={typesQuery.data?.items || []} />
        </div>
      ) : null}

      {activeTab === 'members' ? (
        <div className="membership-tab-content" key="members">
          <MembershipTypeMembersTab
            typeOptions={memberTypeOptions}
            onSelectMember={(membershipUuid) => onNavigate?.(`/store/memberships/customers/${membershipUuid}`)}
          />
        </div>
      ) : null}

      {activeTab === 'actions' ? (
        <div className="membership-tab-content" key="actions">
          <MembershipActionsTab
            tiers={typesQuery.data?.items || []}
            totalMembers={analyticsQuery.data?.activeCount || 0}
            memberCountByType={memberCountByType}
          />
        </div>
      ) : null}
    </section>
  );
};

export default MembershipsOverviewPage;
